import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { X, HeartHandshake, ShieldCheck, Globe2, ExternalLink, Repeat, Zap } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const PRESETS = [950, 2500, 5000, 10000];
const MONTHLY_PLANS = [500, 950, 2500, 5000];
const FEATS_URL = "https://my.featsclub.com/d/I1VFCJ8YiDkW12009XqM";
const EASE = [0.22, 1, 0.36, 1];

export default function DonateModal({ open, onClose, defaultAmount = 950 }) {
  const [tab, setTab] = useState("india");
  const [mode, setMode] = useState("one-time");        // "one-time" | "monthly"
  const [amount, setAmount] = useState(defaultAmount);
  const [monthlyAmt, setMonthlyAmt] = useState(950);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", pan: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [subsEnabled, setSubsEnabled] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/donations/config").then((r) => setSubsEnabled(!!r.data?.subscriptions_enabled)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!subsEnabled && mode === "monthly") setMode("one-time");
  }, [subsEnabled, mode]);

  const finalAmount = mode === "monthly" ? monthlyAmt : (custom ? parseInt(custom, 10) : amount);

  const openRazorpay = (opts, onVerify) => {
    const rzp = new window.Razorpay({
      key: opts.key_id,
      amount: opts.amount,
      currency: opts.currency,
      name: "Akshara Daana",
      description: opts.description,
      image: "/images/akshara-logo.png",
      prefill: { name: form.name, email: form.email, contact: form.phone },
      theme: { color: "#2FBFB1" },
      ...(opts.subscription_id ? { subscription_id: opts.subscription_id } : { order_id: opts.order_id }),
      handler: onVerify,
      modal: { ondismiss: () => setBusy(false) },
    });
    rzp.on("payment.failed", (r) => toast.error(r.error?.description || "Payment failed"));
    rzp.open();
  };

  const payOneTime = async () => {
    const { data } = await api.post("/donations/order", { ...form, amount: finalAmount });
    openRazorpay(
      { key_id: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id, description: "Donation towards rural education" },
      async (resp) => {
        try {
          await api.post("/donations/verify", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          toast.success("Thank you! Your donation was received. A receipt will follow by email.");
          onClose();
        } catch (err) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed — contact us if debited.");
        }
      }
    );
  };

  const payMonthly = async () => {
    const { data } = await api.post("/donations/subscription", { ...form, amount: monthlyAmt });
    openRazorpay(
      { key_id: data.key_id, amount: data.amount, currency: data.currency, subscription_id: data.subscription_id, description: `Monthly donation ₹${monthlyAmt}/mo` },
      async (resp) => {
        try {
          await api.post("/donations/subscription/verify", {
            razorpay_subscription_id: resp.razorpay_subscription_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          toast.success("Thank you! Your monthly commitment is active. A receipt is on its way.");
          onClose();
        } catch (err) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed — contact us if debited.");
        }
      }
    );
  };

  const pay = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) return toast.error("Please fill name, email and phone");
    if (!finalAmount || finalAmount < 1) return toast.error("Please enter a valid amount");
    if (!window.Razorpay) return toast.error("Payment library still loading — please try again in a moment");
    setBusy(true);
    try {
      if (mode === "monthly") await payMonthly();
      else await payOneTime();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start payment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
          data-testid="donate-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white w-full max-w-lg rounded-lg shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative flex items-center justify-between bg-navy text-white px-6 py-5 overflow-hidden">
              <div className="absolute -top-10 -right-6 w-32 h-32 rounded-full bg-teal/30 blur-2xl" />
              <div className="relative flex items-center gap-2 font-display font-extrabold text-lg">
                <HeartHandshake className="w-5 h-5 text-teal" /> Donate to Akshara Daana
              </div>
              <button data-testid="donate-modal-close" onClick={onClose} aria-label="Close" className="relative hover:text-teal transition-colors"><X className="w-5 h-5" /></button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-navy/10">
              <button data-testid="donate-tab-india" onClick={() => setTab("india")}
                className={`flex-1 py-3.5 font-display font-bold text-sm uppercase tracking-widest transition-colors ${tab === "india" ? "bg-white text-teal border-b-2 border-teal" : "bg-cream-soft text-navy/50 hover:text-navy"}`}>
                India (₹)
              </button>
              <button data-testid="donate-tab-intl" onClick={() => setTab("intl")}
                className={`flex-1 py-3.5 font-display font-bold text-sm uppercase tracking-widest transition-colors ${tab === "intl" ? "bg-white text-teal border-b-2 border-teal" : "bg-cream-soft text-navy/50 hover:text-navy"}`}>
                International
              </button>
            </div>

            <AnimatePresence mode="wait">
              {tab === "india" ? (
                <motion.form
                  key="india"
                  onSubmit={pay}
                  className="p-6 space-y-4"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                >
                  {/* One-time / Monthly toggle */}
                  {subsEnabled && (
                    <div className="grid grid-cols-2 p-1 bg-cream-soft rounded-md">
                      <button type="button" data-testid="donate-mode-onetime" onClick={() => setMode("one-time")}
                        className={`flex items-center justify-center gap-2 py-2.5 rounded-sm text-xs uppercase tracking-widest font-display font-bold transition-colors ${mode === "one-time" ? "bg-white text-teal shadow-sm" : "text-navy/50 hover:text-navy"}`}>
                        <Zap className="w-3.5 h-3.5" /> One-time
                      </button>
                      <button type="button" data-testid="donate-mode-monthly" onClick={() => setMode("monthly")}
                        className={`flex items-center justify-center gap-2 py-2.5 rounded-sm text-xs uppercase tracking-widest font-display font-bold transition-colors ${mode === "monthly" ? "bg-white text-teal shadow-sm" : "text-navy/50 hover:text-navy"}`}>
                        <Repeat className="w-3.5 h-3.5" /> Monthly
                      </button>
                    </div>
                  )}

                  {mode === "one-time" ? (
                    <div>
                      <label className="text-[11px] uppercase tracking-widest font-bold text-navy/60">Choose an amount (₹)</label>
                      <div className="mt-2 grid grid-cols-4 gap-2">
                        {PRESETS.map((a) => (
                          <button type="button" key={a} data-testid={`donate-amt-${a}`}
                            onClick={() => { setAmount(a); setCustom(""); }}
                            className={`py-2.5 rounded-sm font-display font-bold text-sm border-2 transition-colors ${!custom && amount === a ? "bg-teal text-white border-teal" : "bg-white text-navy border-navy/15 hover:border-teal"}`}>
                            {a.toLocaleString("en-IN")}
                          </button>
                        ))}
                      </div>
                      <input data-testid="donate-custom" type="number" min="1" placeholder="Or enter a custom amount"
                        value={custom} onChange={(e) => setCustom(e.target.value)}
                        className="mt-2 w-full border border-navy/15 focus:border-teal px-3 py-2.5 rounded-sm font-display text-sm focus:outline-none transition-colors" />
                    </div>
                  ) : (
                    <div>
                      <label className="text-[11px] uppercase tracking-widest font-bold text-navy/60">Choose a monthly plan (₹ / month)</label>
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {MONTHLY_PLANS.map((a) => (
                          <button type="button" key={a} data-testid={`donate-monthly-${a}`}
                            onClick={() => setMonthlyAmt(a)}
                            className={`py-3 rounded-sm font-display font-bold text-sm border-2 transition-colors ${monthlyAmt === a ? "bg-teal text-white border-teal" : "bg-white text-navy border-navy/15 hover:border-teal"}`}>
                            ₹{a.toLocaleString("en-IN")}
                            <span className={`block text-[10px] font-normal mt-0.5 ${monthlyAmt === a ? "text-white/80" : "text-navy/50"}`}>/ month</span>
                          </button>
                        ))}
                      </div>
                      <p className="mt-2 text-[11px] text-navy/60 font-display flex items-start gap-1.5 leading-snug">
                        <Repeat className="w-3.5 h-3.5 text-teal shrink-0 mt-0.5" />
                        Razorpay will auto-debit this amount every month. You can cancel any time from your Razorpay email.
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input data-testid="donate-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-navy/15 focus:border-teal px-3 py-2.5 rounded-sm font-display text-sm focus:outline-none transition-colors" />
                    <input data-testid="donate-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-navy/15 focus:border-teal px-3 py-2.5 rounded-sm font-display text-sm focus:outline-none transition-colors" />
                    <input data-testid="donate-phone" required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border border-navy/15 focus:border-teal px-3 py-2.5 rounded-sm font-display text-sm focus:outline-none transition-colors" />
                    <input data-testid="donate-pan" placeholder="PAN (for 80G receipt)" value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} className="border border-navy/15 focus:border-teal px-3 py-2.5 rounded-sm font-display text-sm focus:outline-none transition-colors" />
                  </div>
                  <button data-testid="donate-pay" type="submit" disabled={busy} className="btn-vermilion w-full justify-center disabled:opacity-60">
                    {busy ? "Starting…" : mode === "monthly"
                      ? `Start ₹${(monthlyAmt || 0).toLocaleString("en-IN")}/mo commitment`
                      : `Donate ₹${(finalAmount || 0).toLocaleString("en-IN")} securely`}
                  </button>
                  <div className="flex items-center justify-center gap-2 text-navy/50 text-xs font-display">
                    <ShieldCheck className="w-4 h-4 text-teal" /> Secured by Razorpay · 80G tax benefit (India)
                  </div>
                </motion.form>
              ) : (
                <motion.div
                  key="intl"
                  className="p-6 space-y-5"
                  data-testid="donate-intl"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 12 }}
                  transition={{ duration: 0.25 }}
                >
                  <p className="font-display text-navy/70 text-sm leading-relaxed">
                    International donors can give in USD through our verified partner platforms. Contributions are
                    tax-deductible (US 501(c)(3) via AarogyaSeva / The Billion Hopes Philanthropic Fund).
                  </p>

                  {/* Feats Club */}
                  <a href={FEATS_URL} target="_blank" rel="noopener noreferrer" data-testid="donate-feats"
                    className="flex items-center justify-between gap-4 bg-navy text-white p-5 rounded-md hover:bg-navy-soft transition-colors group">
                    <div className="flex items-start gap-3">
                      <Globe2 className="w-6 h-6 text-teal shrink-0 mt-0.5" />
                      <div>
                        <div className="font-display font-extrabold text-lg leading-tight">Donate in USD (Card / International)</div>
                        <div className="font-display text-white/70 text-sm mt-0.5">Secure fundraiser via FeatsClub — tax-deductible.</div>
                      </div>
                    </div>
                    <ExternalLink className="w-5 h-5 text-teal group-hover:translate-x-0.5 transition-transform shrink-0" />
                  </a>

                  {/* Zelle */}
                  <div className="border-2 border-dashed border-teal/40 rounded-md p-5 bg-cream-soft">
                    <div className="flex items-center gap-2 font-display font-extrabold text-navy">
                      <span className="text-[#6d1ed4]">Zelle</span> — USA Donors
                    </div>
                    <div className="mt-3 flex flex-col items-center">
                      <div className="w-52 h-52 bg-white border border-navy/10 flex items-center justify-center shrink-0 rounded-md overflow-hidden p-2">
                        <img src="/images/zelle-qr.png" alt="Zelle QR code — AarogyaSeva Global Health Volunteer" className="w-full h-full object-contain"
                          onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.innerHTML = '<span class=\"text-navy/40 text-xs font-display text-center px-2\">Zelle QR<br/>coming soon</span>'; }} />
                      </div>
                      <p className="mt-3 text-center text-navy/70 text-xs font-display max-w-xs leading-relaxed">
                        Scan the QR in your bank's Zelle app to donate. Payments route to our US 501(c)(3) partner AarogyaSeva.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-navy/50 text-xs font-display">
                    <ShieldCheck className="w-4 h-4 text-teal" /> 501(c)(3) tax-deductible (USA)
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
